import {
  registerDecorator,
  type ValidationOptions,
  type ValidationArguments,
} from 'class-validator';

const FOLLOWUP_ACTION_TYPES = ['remain', 'default', 'redirect', 'landing'] as const;

/**
 * Validates that a value is a valid form follow-up action (discriminated union).
 *
 * Valid shapes:
 * - { type: 'remain' }
 * - { type: 'default' }
 * - { type: 'redirect', url: string }
 * - { type: 'landing', pageId?: string, url: string }
 */
export function validateFollowupAction(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const record = value as Record<string, unknown>;

  if (!('type' in record) || typeof record.type !== 'string') {
    return false;
  }

  if (!FOLLOWUP_ACTION_TYPES.includes(record.type as (typeof FOLLOWUP_ACTION_TYPES)[number])) {
    return false;
  }

  if (record.type === 'redirect') {
    if (!('url' in record) || typeof record.url !== 'string') {
      return false;
    }
  }

  if (record.type === 'landing') {
    // pageId is optional (can be undefined when no pages are available)
    if ('pageId' in record && record.pageId !== undefined && typeof record.pageId !== 'string') {
      return false;
    }
    if (!('url' in record) || typeof record.url !== 'string') {
      return false;
    }
  }

  return true;
}

export function IsFollowupAction(validationOptions?: ValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'isFollowupAction',
      target: object.constructor,
      propertyName: propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown) {
          return validateFollowupAction(value);
        },
        defaultMessage(args: ValidationArguments) {
          return `${args.property} must be a valid follow-up action: { type: 'remain' | 'default' } or { type: 'redirect', url: string } or { type: 'landing', pageId?: string, url: string }`;
        },
      },
    });
  };
}
